import {WithSyncableStateFeatureOptions} from '../features/shared/with-syncable-state-feature.types';
import * as AllStoreDefinitions from './signal-stores-config';

/**
 * Represents the state of a signal store.
 * @template T - The type of the state object.
 */
export type SignalStoreState<T extends object = object> = {[K in keyof T]: T[K]} & {};

/**
 * Represents the definition of a signal store.
 *
 * @template S - The type of the signal store state.
 * @template SignalStoreConfig - The type of the signal store config.
 */
declare type SignalStoreDefinition<S extends SignalStoreState = SignalStoreState> = {
	initialState: S;
	config?: SignalStoreConfig;
};

export type SignalStoreConfig = {
	lastLoadAt?: Date;
	clearOn: WithSyncableStateFeatureOptions['clearOn'];
};

/**
 * Defines the signal store state by specifying the initial state.
 * @template State - The type of the signal store state.
 * @param initialState - The initial state of the signal store.
 * @param config
 * @returns The signal store definition.
 */
export function signalStoreDefinition<State extends SignalStoreState = SignalStoreState>(initialState: State, config?: SignalStoreConfig): SignalStoreDefinition<State> {
	return Object.freeze({initialState, config});
}

/* -------------------------- Base Structure types -------------------------- */

/**
 * Represents the type of the signal store name.
 */
export type SignalStoreNameType = keyof typeof AllStoreDefinitions;

/**
 * Represents the structure of the signal store state for a given store name.
 *
 * @template StoreName - The type of the store name.
 */
export type SignalStoreStateStructure<StoreName extends SignalStoreNameType> = SignalStoreDefinition<(typeof AllStoreDefinitions)[StoreName]>['initialState']['initialState'];

/**
 * Represents the global signal store state.
 */
export type GlobalSignalStoreState = {
	[K in SignalStoreNameType]: {
		state: SignalStoreDefinition<(typeof AllStoreDefinitions)[K]>['initialState'];
		config: SignalStoreConfig;
	};
};

export type GlobalsSignalStoresConfig = {
	[K in SignalStoreNameType]: SignalStoreConfig;
};

/* ------------------------------------ / ----------------------------------- */

/**
 * Gets the store name token for the specified store name type.
 * @template StoreName - The type of the store name.
 * @param storeName - The name of the store.
 * @returns The store name token.
 */
export function getSignalStoreNameToken<StoreName extends SignalStoreNameType>(storeName: StoreName): StoreName {
	return storeName as StoreName;
}

/**
 * Gets the initial state of the specified store name.
 * @template StoreName - The type of the store name.
 * @param storeName - The name of the store.
 * @returns The initial state of the store.
 */
export function initialStateOf<StoreName extends SignalStoreNameType>(storeName: StoreName): SignalStoreStateStructure<StoreName> {
	return AllStoreDefinitions[storeName].initialState;
}

/**
 * The initial state of the global signal store.
 */
export const globalSignalStoreInitialState = Object.freeze(
	Object.entries(AllStoreDefinitions).reduce((acc, storeDefEntry) => {
		const [storeName, storeDef] = storeDefEntry;
		if (storeName && storeDef && storeDef?.initialState) {
			acc[storeName] = {
				state: Object.freeze({...storeDef.initialState}),
				config: storeDef?.config || {clearOn: 'logout'},
			};
		}
		return acc;
	}, {} as GlobalSignalStoreState),
);
